import * as THREE from 'three';
import type { PoiConfig } from '../../poiConfig';
import type { Sight, SightId } from '../../types';
import { CollisionSystem } from '../collision/CollisionSystem';

export interface PoiMarkerDiagnostics {
  sightId: SightId;
  distance: number;
  visible: boolean;
  occluded: boolean;
}

interface PoiMarker {
  element: HTMLButtonElement;
  position: THREE.Vector3;
  lastX: number;
  lastY: number;
  hidden: boolean;
  isFocused: boolean;
  isRight: boolean;
  sightId: SightId;
  distance: number;
  occluded: boolean;
}

interface PoiMarkerManagerOptions {
  container: HTMLElement;
  gestureTarget: HTMLElement;
  collisionSystem: CollisionSystem;
  onNavigateToSight: (sightId: SightId) => void;
  initialConfig: PoiConfig;
}

const POI_MARKER_HIDDEN_CLASS = 'is-hidden';
const POI_MARKER_OCCLUDED_CLASS = 'is-occluded';
const POI_MARKER_FOCUSED_CLASS = 'is-focused';
const POI_MARKER_RIGHT_CLASS = 'is-right';
const POI_MARKER_CENTERING_TRANSFORM = ' translate(-50%, -50%)';
const POI_GESTURE_DRAG_THRESHOLD_PX = 5;

export class PoiMarkerManager {
  private readonly poiLayer: HTMLDivElement;
  private readonly collisionSystem: CollisionSystem;
  private readonly onNavigateToSight: (sightId: SightId) => void;
  private readonly gestureTarget: HTMLElement;
  private markers: PoiMarker[] = [];
  private cameraDirection = new THREE.Vector3();
  private markerVector = new THREE.Vector3();
  private markerToCamera = new THREE.Vector3();
  private lineOfSightTarget = new THREE.Vector3();
  private markerDirectionToCamera = new THREE.Vector3();
  private config: PoiConfig;
  private focusedSightId: SightId | null = null;
  private readonly markerLookup = new Map<HTMLButtonElement, PoiMarker>();
  private readonly pointerStates = new Map<
    number,
    {
      startX: number;
      startY: number;
      moved: boolean;
      element: HTMLButtonElement;
      deferredDownInit: PointerEventInit;
    }
  >();
  private readonly suppressClickElements = new WeakSet<HTMLButtonElement>();

  constructor(options: PoiMarkerManagerOptions) {
    this.collisionSystem = options.collisionSystem;
    this.onNavigateToSight = options.onNavigateToSight;
    this.gestureTarget = options.gestureTarget;
    this.config = options.initialConfig;

    this.poiLayer = document.createElement('div');
    this.poiLayer.className = 'scene-poi-layer';
    this.poiLayer.dir = 'ltr';
    options.container.appendChild(this.poiLayer);
  }

  setConfig(config: PoiConfig): void {
    this.config = config;
  }

  setFocusedSightId(sightId: SightId | null): void {
    this.focusedSightId = sightId;
  }

  createMarkers(sights: Sight[]): void {
    if (sights.length === 0) {
      return;
    }
    sights.forEach((sight) => {
      const position = sight.position;
      if (!position) {
        return;
      }
      const markerElement = document.createElement('button');
      markerElement.type = 'button';
      markerElement.className = `scene-poi-marker ${POI_MARKER_HIDDEN_CLASS}`;
      markerElement.dataset.sightId = sight.id.toString();
      markerElement.addEventListener('pointerdown', this.handleMarkerPointerDown);
      markerElement.addEventListener('pointermove', this.handleMarkerPointerMove);
      markerElement.addEventListener('pointerup', this.handleMarkerPointerUp);
      markerElement.addEventListener('pointercancel', this.handleMarkerPointerCancel);
      markerElement.addEventListener('click', this.handleMarkerClick);
      const dot = document.createElement('span');
      dot.className = 'scene-poi-marker__dot';
      dot.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.className = 'scene-poi-marker__label';
      label.dir = document.documentElement.dir;
      label.textContent = sight.name ?? sight.id.toString();
      markerElement.append(dot, label);
      this.poiLayer.appendChild(markerElement);
      const marker: PoiMarker = {
        element: markerElement,
        position: new THREE.Vector3(position.x, position.y, position.z),
        lastX: Number.NaN,
        lastY: Number.NaN,
        hidden: true,
        isFocused: false,
        isRight: false,
        sightId: sight.id,
        distance: Number.POSITIVE_INFINITY,
        occluded: false,
      };
      this.markers.push(marker);
      this.markerLookup.set(markerElement, marker);
    });
  }

  private handleMarkerPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }
    const element = event.currentTarget as HTMLButtonElement | null;
    if (!element) {
      return;
    }
    this.pointerStates.set(event.pointerId, {
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      element,
      deferredDownInit: this.buildForwardEventInit(event),
    });
    if (element.setPointerCapture) {
      try {
        element.setPointerCapture(event.pointerId);
      } catch {
        // Ignore capture failures.
      }
    }
  };

  private handleMarkerPointerMove = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    if (Math.hypot(deltaX, deltaY) < POI_GESTURE_DRAG_THRESHOLD_PX) {
      return;
    }
    state.moved = true;
    this.suppressClickElements.add(state.element);
    this.pointerStates.delete(event.pointerId);
    try {
      state.element.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may have already been released.
    }
    this.gestureTarget.dispatchEvent(
      new PointerEvent('pointerdown', state.deferredDownInit),
    );
    this.forwardPointerEvent('pointermove', event);
  };

  private handleMarkerPointerUp = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    this.pointerStates.delete(event.pointerId);
    try {
      state.element.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may have already been released.
    }
    this.suppressClickElements.add(state.element);
    if (!state.moved) {
      const marker = this.markerLookup.get(state.element);
      if (marker) {
        this.onNavigateToSight(marker.sightId);
      }
    }
  };

  private handleMarkerPointerCancel = (event: PointerEvent): void => {
    const state = this.pointerStates.get(event.pointerId);
    if (!state) {
      return;
    }
    this.pointerStates.delete(event.pointerId);
    this.suppressClickElements.add(state.element);
    if (state.moved) {
      this.forwardPointerEvent('pointercancel', event);
    }
  };

  private handleMarkerClick = (event: MouseEvent): void => {
    const element = event.currentTarget as HTMLButtonElement | null;
    if (!element) {
      return;
    }
    if (this.suppressClickElements.has(element)) {
      this.suppressClickElements.delete(element);
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const marker = this.markerLookup.get(element);
    if (marker) {
      this.onNavigateToSight(marker.sightId);
    }
  };

  private buildForwardEventInit(event: PointerEvent): PointerEventInit {
    return {
      bubbles: true,
      cancelable: true,
      composed: true,
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      isPrimary: event.isPrimary,
      clientX: event.clientX,
      clientY: event.clientY,
      screenX: event.screenX,
      screenY: event.screenY,
      button: event.button,
      buttons: event.buttons,
      pressure: event.pressure,
      width: event.width,
      height: event.height,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
    };
  }

  private forwardPointerEvent(type: string, event: PointerEvent): void {
    this.gestureTarget.dispatchEvent(
      new PointerEvent(type, this.buildForwardEventInit(event)),
    );
  }

  update(camera: THREE.PerspectiveCamera, canvasWidth: number, canvasHeight: number): void {
    const markers = this.markers;
    const markerCount = markers.length;
    if (markerCount === 0) {
      return;
    }
    this.cameraDirection.set(0, 0, -1).applyQuaternion(camera.quaternion).normalize();
    const widthHalf = canvasWidth * 0.5;
    const heightHalf = canvasHeight * 0.5;
    const cameraPosition = camera.position;
    const probeMaxOffset = Math.max(0, this.config.occlusionProbeMaxOffset);
    const probeMinDistance = Math.max(0, this.config.occlusionProbeMinDistance);

    for (let i = 0; i < markerCount; i += 1) {
      const marker = markers[i];
      const { position, element } = marker;
      this.markerVector.copy(position).project(camera);
      this.markerToCamera.subVectors(position, cameraPosition);
      const markerDistance = this.markerToCamera.length();

      const clipX = this.markerVector.x;
      const clipY = this.markerVector.y;
      const clipZ = this.markerVector.z;
      const isBehindCamera = this.markerToCamera.dot(this.cameraDirection) <= 0;
      const outsideFrustum =
        clipX < -1 ||
        clipX > 1 ||
        clipY < -1 ||
        clipY > 1 ||
        clipZ < -1 ||
        clipZ > 1;
      let shouldHide = isBehindCamera || outsideFrustum;
      let occludedByCollision = false;

      if (!shouldHide) {
        this.lineOfSightTarget.copy(position);
        if (markerDistance > probeMinDistance) {
          const maxProbe = markerDistance - probeMinDistance;
          const probeDistance = Math.min(maxProbe, probeMaxOffset);
          if (probeDistance > 0) {
            this.markerDirectionToCamera.copy(this.markerToCamera).normalize();
            this.lineOfSightTarget.addScaledVector(this.markerDirectionToCamera, -probeDistance);
          }
        }
        const occluded = this.collisionSystem.isLineOfSightObstructed(cameraPosition, this.lineOfSightTarget);
        occludedByCollision = occluded;
      }

      if (marker.occluded !== occludedByCollision) {
        marker.occluded = occludedByCollision;
        element.classList.toggle(POI_MARKER_OCCLUDED_CLASS, occludedByCollision);
      }
      marker.distance = markerDistance;

      const isFocused = this.focusedSightId !== null && marker.sightId === this.focusedSightId;
      if (marker.isFocused !== isFocused) {
        marker.isFocused = isFocused;
        element.classList.toggle(POI_MARKER_FOCUSED_CLASS, isFocused);
      }

      if (shouldHide) {
        if (!marker.hidden) {
          marker.hidden = true;
          element.classList.add(POI_MARKER_HIDDEN_CLASS);
        }
        continue;
      }

      if (marker.hidden) {
        marker.hidden = false;
        element.classList.remove(POI_MARKER_HIDDEN_CLASS);
      }

      const rawScreenX = clipX * widthHalf + widthHalf;
      const rawScreenY = -clipY * heightHalf + heightHalf;
      const screenX = +rawScreenX.toFixed(2);
      const screenY = +rawScreenY.toFixed(2);

      if (marker.lastX !== screenX || marker.lastY !== screenY) {
        marker.lastX = screenX;
        marker.lastY = screenY;
        element.style.transform = `translate3d(${screenX}px, ${screenY}px, 0)${POI_MARKER_CENTERING_TRANSFORM}`;
      }

      // const isRight = rawScreenX > canvasWidth * 0.75;
      // if (marker.isRight !== isRight) {
      //   marker.isRight = isRight;
      //   element.classList.toggle(POI_MARKER_RIGHT_CLASS, isRight);
      // }
    }
  }

  getDiagnostics(): PoiMarkerDiagnostics[] {
    if (this.markers.length === 0) {
      return [];
    }
    return this.markers.map((marker) => ({
      sightId: marker.sightId,
      distance: marker.distance,
      visible: !marker.hidden,
      occluded: marker.occluded,
    }));
  }

  dispose(): void {
    this.markers.splice(0).forEach((marker) => {
      marker.element.remove();
    });
    this.poiLayer.remove();
  }
}
